import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMySubscriptionApi,
  unsubscribeApi,
} from "../http/http";
import {
  clearAuthSession,
  getAuthToken,
  getAuthUser,
  isAuthenticated,
  isSubscribedUser,
  updateAuthUser,
} from "../util/auth";

export const useAuth = () => {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [subscribed, setSubscribed] = useState(isSubscribedUser());
  const [user, setUser] = useState(getAuthUser());
  const [actionLoading, setActionLoading] = useState(false);

  const syncAuthState = useCallback(() => {
    setLoggedIn(isAuthenticated());
    setSubscribed(isSubscribedUser());
    setUser(getAuthUser());
  }, []);

  const refreshSubscription = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      syncAuthState();
      return;
    }

    try {
      const result = await getMySubscriptionApi(token);
      const isSubscribed = Boolean(result?.data?.isSubscribed);

      updateAuthUser({ isSubscribed });
      setSubscribed(isSubscribed);
      setUser(getAuthUser());
    } catch {
      syncAuthState();
    }
  }, [syncAuthState]);

  useEffect(() => {
    syncAuthState();

    if (isAuthenticated()) {
      refreshSubscription();
    }
  }, [refreshSubscription, syncAuthState]);

  useEffect(() => {
    const onStorage = () => syncAuthState();
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [syncAuthState]);

  const logout = useCallback(() => {
    clearAuthSession();
    syncAuthState();
    navigate("/");
  }, [navigate, syncAuthState]);

  const handleUnsubscribe = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      navigate("/login");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to unsubscribe? Access will end after operator confirmation."
    );

    if (!confirmed) {
      return;
    }

    setActionLoading(true);

    try {
      await unsubscribeApi(token);
      updateAuthUser({ isSubscribed: false });
      clearAuthSession();
      syncAuthState();
      alert(
        "Unsubscription request sent via VDL. You will be logged out once the operator confirms."
      );
      navigate("/");
    } catch (error) {
      alert(
        error.response?.data?.message ||
          "Unsubscription failed. Please try again."
      );
    } finally {
      setActionLoading(false);
    }
  }, [navigate, syncAuthState]);

  const goToSubscribe = useCallback(() => {
    navigate("/subscribe");
  }, [navigate]);

  const goToLogin = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  return {
    loggedIn,
    subscribed,
    user,
    actionLoading,
    logout,
    handleUnsubscribe,
    goToSubscribe,
    goToLogin,
    refreshSubscription,
    syncAuthState,
  };
};
